// Component to control InfoModal component

import {
  useState,
  useContext,
  useRef,
  createContext,
  useEffect,
  Fragment,
} from 'react';
import InfoModal from './InfoModal';

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

export default function ModalProvider({ children }) {
  const [show, setShow] = useState(false);
  const [header, setHeader] = useState('');
  const [body, setBody] = useState();
  // InfoModal hides the body instead of unmounting it, so a reopened form
  // would still hold the previous edits. Counted rather than keyed by the
  // content: the same body opened twice also needs a fresh mount
  const [bodyKey, setBodyKey] = useState(0);
  const [buttons, setButtons] = useState([]);
  const [modalAncorRef, setModalAncorRef] = useState(null);
  const promiseResolve = useRef(null);
  const onCloseCallback = useRef(null);

  const openModal = async ({
    header,
    onClose,
    body,
    buttons = [],
    ancorRef,
  }) => {
    setHeader(header);
    onCloseCallback.current = onClose;
    setBody(body);
    setBodyKey((k) => k + 1);
    setButtons(buttons);
    setModalAncorRef(ancorRef || null);
    setShow(true);

    return new Promise((resolve) => {
      promiseResolve.current = resolve;
    });
  };

  const closeModal = () => setShow(false);

  useEffect(() => {
    if (!show && promiseResolve.current) {
      promiseResolve.current();
      promiseResolve.current = null;
    }
  }, [show]);

  const handleClose = () => {
    if (onCloseCallback.current) onCloseCallback.current();
    setShow(false);
  };

  const buttonHandlers = buttons.map((btn) => ({
    ...btn,
    onClick: () => {
      if (btn.onClick) btn.onClick();
      setShow(false);
    },
  }));

  return (
    // `show` travels with the API because InfoModal hides the body instead of
    // unmounting it: a body holding a listener or a timer has no other way to
    // learn that nobody is looking at it any more
    <ModalContext.Provider value={{ openModal, closeModal, isModalOpen: show }}>
      {children}
      <InfoModal
        show={show}
        onClose={handleClose}
        header={header}
        body={<Fragment key={bodyKey}>{body}</Fragment>}
        buttons={buttonHandlers}
        ancorRef={modalAncorRef}
      />
    </ModalContext.Provider>
  );
}
